import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Target, CheckCircle } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="bg-charcoal min-h-screen pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="py-24 lg:py-32 border-b border-border-dark relative">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1723117509275-552bc072e117?auto=format&fit=crop&w=1920&q=80')`,
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl"
          >
            <p className="text-bronze text-mono text-sm tracking-widest mb-4">ABOUT STANDARD COMPOSITES</p>
            <h1 className="heading-display text-stone text-5xl md:text-6xl mb-8">
              INFRASTRUCTURE DESERVES<br />
              <span className="text-bronze">BETTER MATERIALS.</span>
            </h1>
            <p className="text-stone/60 text-xl leading-relaxed">
              Standard Composites exists to supply the construction industry with advanced composite reinforcement materials that are available, reliable, and specification-ready.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 lg:py-24" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <Target size={24} className="text-bronze" />
                <p className="text-bronze text-mono text-sm tracking-widest">OUR MISSION</p>
              </div>
              <h2 className="heading-display text-stone text-4xl mb-6">
                BUILDING FOR THE LONG TERM
              </h2>
              <p className="text-stone/70 leading-relaxed mb-6">
                The infrastructure we build today will serve communities for generations. The materials we choose determine whether those structures endure or deteriorate.
              </p>
              <p className="text-stone/70 leading-relaxed">
                Standard Composites delivers advanced composite reinforcement that eliminates the corrosion failure mode of traditional steel. We source premium raw materials, partner with certified manufacturers, and stand behind every product that ships under our name.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-square bg-gradient-to-br from-bronze/20 via-surface-dark to-charcoal border border-border-dark" />
              <div className="absolute inset-8 border border-bronze/30 flex items-center justify-center">
                <div className="text-center">
                  <span className="text-bronze text-mono text-6xl font-display">GEO</span>
                  <p className="text-stone/50 text-sm mt-2">PRODUCT FAMILY</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Supply Chain Philosophy */}
      <section className="py-16 lg:py-24 bg-surface-dark" data-testid="supply-chain-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <Shield size={24} className="text-bronze" />
              <p className="text-bronze text-mono text-sm tracking-widest">SUPPLY CHAIN</p>
            </div>
            <h2 className="heading-display text-stone text-4xl mb-6">
              QUALITY AT EVERY STEP
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Raw Material Sourcing',
                description: 'We source the best fiber rovings from proven global producers. Our basalt comes from certified volcanic sources; our E-glass from established industrial suppliers.'
              },
              {
                title: 'Manufacturing Partners',
                description: 'Products are manufactured by vetted, ISO-certified toll manufacturing facilities under strict quality protocols. We maintain oversight of every production run.'
              },
              {
                title: 'Quality Assurance',
                description: 'Every shipment is tested and certified to meet ASTM specifications. We stand behind what ships under the Standard Composites label — period.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 bg-charcoal border border-border-dark"
              >
                <h3 className="heading-section text-stone text-xl mb-4">{item.title}</h3>
                <p className="text-stone/60 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The GEO Product Family */}
      <section className="py-16 lg:py-24" data-testid="geo-family-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <p className="text-bronze text-mono text-sm tracking-widest mb-4">THE NAME</p>
            <h2 className="heading-display text-stone text-4xl mb-6">
              WHY "GEO"?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Geological Origin',
                description: 'Basalt fiber is derived from volcanic rock — one of the most abundant and durable materials on Earth. GEO honors this geological foundation.'
              },
              {
                title: 'Geotechnical Application',
                description: 'Our products are designed for infrastructure, ground contact, and civil engineering applications. GEO reflects this geotechnical purpose.'
              },
              {
                title: 'Geographic Permanence',
                description: 'The structures built with GEO products are meant to last. GEO represents the permanence and stability we deliver to every project.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="border-l-4 border-bronze pl-6"
              >
                <h3 className="heading-section text-stone text-xl mb-3">{item.title}</h3>
                <p className="text-stone/60 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment to Standards */}
      <section className="py-16 lg:py-24 bg-surface-dark" data-testid="standards-commitment-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-bronze text-mono text-sm tracking-widest mb-4">COMPLIANCE</p>
              <h2 className="heading-display text-stone text-4xl mb-6">
                COMMITMENT TO STANDARDS
              </h2>
              <p className="text-stone/70 leading-relaxed mb-6">
                Every Standard Composites product is designed and manufactured to meet or exceed applicable ASTM and ACI standards. Our manufacturing partners maintain ISO certifications and rigorous quality management systems.
              </p>
              <p className="text-stone/70 leading-relaxed">
                When you specify GEO products, you're specifying materials that engineers, inspectors, and owners can trust.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-charcoal border border-border-dark p-8"
            >
              <h3 className="heading-section text-stone text-xl mb-6">Standards Compliance</h3>
              <ul className="space-y-4">
                {[
                  'ASTM D7205 — Tensile Test Method',
                  'ASTM D7957 — GFRP Specification',
                  'ACI 440.1R — FRP Design Guide',
                  'ACI 440.11-22 — GFRP Building Code',
                  'ISO 9001 — Manufacturing Partners'
                ].map((standard, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle size={18} className="text-bronze flex-shrink-0" />
                    <span className="text-stone/70 text-sm">{standard}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 border-t border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="heading-display text-stone text-3xl md:text-4xl mb-6">
              READY TO BUILD BETTER?
            </h2>
            <p className="text-stone/60 max-w-xl mx-auto mb-8">
              Explore our products or contact our team to discuss your next project.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/products" 
                className="btn btn-outline"
                data-testid="about-cta-products"
              >
                View Products
              </Link>
              <Link 
                to="/contact" 
                className="btn btn-primary"
                data-testid="about-cta-contact"
              >
                Contact Us
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
